import { ChangeEvent, FC, useRef, useState } from "react"
import { Tip, TipType } from "../Tip/Tip"
import s from "./UpdateStoks.module.css"
import JSZip from "jszip"
import { Modal } from "../../features/Modal/Modal"
import { prepareXLSX, prepareZip } from "./helpers"
import { TextField, Button, Flex, Text } from "@radix-ui/themes"
import { StorageSelectControl } from "../StorageSelect/StorageSelectControl"
import { MARKETPLACE } from "@acc/shared"

type UploadMultipleFilesFromKatrenProps = {
  deleteDataFromKatren: () => void
  isDataFromKatrenUploaded: boolean
  uploadDataFromKatren: (
    data: {
      name: string
      amount: number
      price: number
      priceInfo?: { price: number; storage?: string }[]
    }[],
    fileName?: string,
  ) => void
}

export const UploadMultipleFilesFromKatren: FC<
  UploadMultipleFilesFromKatrenProps
> = ({
  deleteDataFromKatren,
  isDataFromKatrenUploaded,
  uploadDataFromKatren,
}) => {
  const [isOpen, setOpen] = useState(false)
  const [files, setFiles] = useState<{
    [key: string]: JSZip.JSZipObject & { storage?: string }
  }>({})
  const [mainFileName, setMainFileName] = useState("")
  const [isMaxDiffMethod, setMaxDiffMethod] = useState(true)
  const inputFiles = useRef<HTMLInputElement>(null)
  const [maxDiff, setMaxDiff] = useState<{
    num: number | null
    percent: number | null
  }>({ num: null, percent: null })

  return (
    <div className={s.uploadFileWrap}>
      {isDataFromKatrenUploaded ? (
        <div>
          <Button onClick={deleteDataFromKatren}>
            удалить файл цен и остатков с Катрена
          </Button>
        </div>
      ) : (
        <div>
          <Button asChild>
            <label htmlFor={s.pricesFromKtrnInp}>
              Загрузить zip-файл цен и остатков с Катрена
              <input
                style={{ display: "none" }}
                ref={inputFiles}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  e.target.files &&
                    prepareZip(e.target.files[0]).then((files) => {
                      setFiles(files)
                      setOpen(true)
                      inputFiles.current && (inputFiles.current.value = "")
                      // uploadDataFromKatren(data)
                    })
                }}
                type="file"
                accept=".zip"
                id={s.pricesFromKtrnInp}
              />
            </label>
          </Button>
        </div>
      )}
      <Tip
        content={
          <span>
            <b>Внимание!</b>
            <p>
              Прикрепляемый файл должен быть в формате <b>ZIP</b>. В архиве
              должны быть файлы цен и остатков в формате <b>XLSX</b>. XLSX-файлы
              должны содержать следующие <b>обязательные</b> колонки:
            </p>
            <ol className={s.list}>
              <li>
                <b>Название</b> - название продукта
              </li>
              <li>
                <b>Остаток</b> - остаток на складе катрена
              </li>
              <li>
                <b>Цена за 1 уп.</b> - цена
              </li>
            </ol>
          </span>
        }
        type={TipType.WARNING}
      />
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setOpen(false)
          setFiles({})
          setMainFileName("")
        }}
        onOk={() => {
          prepareXLSX(
            files,
            mainFileName,
            (isMaxDiffMethod && maxDiff.num) || undefined,
            (isMaxDiffMethod && maxDiff.percent) || undefined,
          ).then(({ data, fileName }) => uploadDataFromKatren(data, fileName))
        }}
        isOkDisabled={
          !mainFileName ||
          (isMaxDiffMethod && (!maxDiff.num || !maxDiff.percent)) ||
          !Object.keys(files).length ||
          Object.keys(files).some((key) => !files[key]?.storage)
        }
        header="Выберите параметры загрузки"
        content={
          <div className="flex flex-column gap-10">
            <div className="flex flex-column gap-10">
              <Text weight="bold">
                Выберите файл из которого загрузить остатки и сопоставьте склады
              </Text>
              {Object.keys(files).map((key) => (
                <Flex key={key} gap="2" justify="between">
                  <Flex align="center">
                    <input
                      type="radio"
                      id={key}
                      name={key}
                      value={key}
                      onChange={() => {
                        setMainFileName(key)
                      }}
                      checked={key === mainFileName}
                    />
                    <Text as="label" htmlFor={key}>
                      {key}
                    </Text>
                  </Flex>
                  <Flex>
                    <StorageSelectControl
                      value={files[key].storage || ""}
                      onChange={(value) => {
                        files[key].storage = value
                        setFiles({ ...files })
                      }}
                      controlKey={"storages-select" + key}
                      marketplace={MARKETPLACE.WB}
                    />
                  </Flex>
                </Flex>
              ))}
            </div>
            <div className="flex flex-column gap-10">
              <Text weight="bold">Выберите способ формирования цен</Text>

              <div id="pricecalcmethod" className="flex flex-column gap-10">
                <Text as="label" htmlFor="maxDiffMethod">
                  <input
                    id="maxDiffMethod"
                    type="radio"
                    checked={isMaxDiffMethod}
                    onChange={() => setMaxDiffMethod(true)}
                  />
                  Установить максимальную разницу цен
                </Text>
                <Text as="label" htmlFor="maxPrice">
                  <input
                    id="maxPrice"
                    type="radio"
                    checked={!isMaxDiffMethod}
                    onChange={() => setMaxDiffMethod(false)}
                  />
                  Максимальная цена
                </Text>
              </div>
              {(isMaxDiffMethod && (
                <div className="flex flex-column gap-10">
                  <label htmlFor="maxDiff">
                    Максимальная разница цен
                    <TextField.Root
                      size="3"
                      id="maxDiff"
                      type="number"
                      value={maxDiff.num || ""}
                      onChange={(e) =>
                        setMaxDiff({
                          num: Number(e.target.value) || null,
                          percent: maxDiff.percent,
                        })
                      }
                    />
                  </label>

                  <label htmlFor="maxDiff">
                    Максимальная разница цен в процентах
                    <TextField.Root
                      size="3"
                      id="maxDiff"
                      type="number"
                      value={maxDiff.percent || ""}
                      onChange={(e) =>
                        setMaxDiff({
                          num: maxDiff.num,
                          percent: Number(e.target.value) || null,
                        })
                      }
                    />
                  </label>
                </div>
              )) ||
                null}
            </div>
          </div>
        }
      ></Modal>
    </div>
  )
}
